import { useMemo, useState, Fragment } from "react";
import { TESTIDS } from "@/constants/testIds";
import { ChevronDown, ChevronRight } from "lucide-react";

const ATTR_KEYS = [
  ["attr_1_property_mentioned", "Mentioned"],
  ["attr_2_official_page_cited", "Cited"],
  ["attr_3_property_content_answered", "Owned"],
  ["attr_4_ota_mentioned_property", "OTA"],
  ["attr_5_competing_hotel_appeared", "Competitor"],
  ["attr_6_publisher_dominated", "Publisher"],
  ["attr_7_content_opportunity", "Opp"],
];

function AttrDot({ on, invert }) {
  const good = invert ? !on : on;
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${
        good
          ? "bg-[color:var(--score-high)]"
          : on
            ? "bg-[color:var(--score-low)]"
            : ""
      }`}
      style={good ? {} : on ? {} : { background: "var(--border-strong)" }}
    />
  );
}

function scoreClass(s) {
  if (s >= 70) return "pill pill-won";
  if (s >= 35) return "pill pill-partial";
  return "pill pill-lost";
}

export default function EvidenceTable({ queries, platformResults }) {
  const [expanded, setExpanded] = useState(new Set());
  const [platformFilter, setPlatformFilter] = useState("all");

  // group results by query_id
  const resultsByQ = useMemo(() => {
    const m = new Map();
    for (const r of platformResults) {
      if (!m.has(r.query_id)) m.set(r.query_id, []);
      m.get(r.query_id).push(r);
    }
    return m;
  }, [platformResults]);

  const rows = useMemo(() => {
    return queries.map((q, idx) => {
      const prs = resultsByQ.get(q.id) || [];
      const filtered = platformFilter === "all" ? prs : prs.filter((r) => r.platform === platformFilter);
      const avg = filtered.length
        ? filtered.reduce((s, r) => s + r.per_query_score, 0) / filtered.length
        : 0;
      return { q, prs, filtered, avg, idx };
    });
  }, [queries, resultsByQ, platformFilter]);

  const toggle = (qid) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(qid)) n.delete(qid);
      else n.add(qid);
      return n;
    });
  };

  return (
    <div className="glass rounded-2xl p-6 md:p-8" data-testid={TESTIDS.evidenceTable}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow mb-1">Query-level evidence</div>
          <div className="font-serif text-2xl">All {queries.length} queries · {platformResults.length} platform runs</div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {["all", "chatgpt", "google_ai", "perplexity"].map((p) => (
            <button
              key={p}
              onClick={() => setPlatformFilter(p)}
              className={`px-3 py-1.5 rounded-full border transition-colors ${
                platformFilter === p
                  ? "border-[color:var(--brand-sage)] text-[color:var(--brand-sage-deep)] bg-[color:var(--brand-sage-muted)]"
                  : "border-[color:var(--border-default)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:border-[color:var(--border-strong)]"
              }`}
              data-testid={`evidence-filter-${p}`}
            >
              {p === "all" ? "All" : p === "chatgpt" ? "ChatGPT" : p === "google_ai" ? "Google AI" : "Perplexity"}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="evidence-table min-w-[900px]">
          <thead>
            <tr>
              <th style={{ width: "36px" }}></th>
              <th>Query</th>
              <th>Category</th>
              <th>Importance</th>
              <th>Avg Score</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ q, prs, filtered, avg, idx }) => {
              const isOpen = expanded.has(q.id);
              return (
                <Fragment key={q.id}>
                  <tr
                    className="cursor-pointer"
                    onClick={() => toggle(q.id)}
                    data-testid={TESTIDS.evidenceRow(idx)}
                  >
                    <td>
                      {isOpen ? (
                        <ChevronDown className="w-4 h-4" style={{ color: "var(--brand-sage-deep)" }} />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-[color:var(--text-muted)]" />
                      )}
                    </td>
                    <td className="font-mono text-xs md:text-sm max-w-md">{q.text}</td>
                    <td className="text-xs text-[color:var(--text-secondary)]">
                      {q.category_label}
                    </td>
                    <td>
                      <span
                        className={`pill ${
                          q.importance_tier === "High"
                            ? "pill-gold"
                            : q.importance_tier === "Standard"
                              ? "pill-neutral"
                              : "pill-neutral"
                        }`}
                      >
                        {q.importance_tier}
                      </span>
                    </td>
                    <td className="num">
                      <span className={scoreClass(avg)}>{Math.round(avg)}</span>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5} className="!p-0">
                        <div className="p-4 md:p-6 border-l-2 mx-2 mb-3 rounded-r-xl" style={{ background: "var(--bg-secondary)", borderColor: "rgba(107,122,85,0.5)" }}>
                          <div className="grid gap-3">
                            {filtered.map((pr) => (
                              <div key={pr.id} className="glass rounded-xl p-4">
                                <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                                  <div className="flex items-center gap-3">
                                    <span className="label-eyebrow" style={{ color: "var(--brand-sage-deep)" }}>
                                      {pr.platform_label}
                                    </span>
                                    <span className="text-xs text-[color:var(--text-muted)]">
                                      Confidence: {pr.confidence}
                                    </span>
                                  </div>
                                  <span className={scoreClass(pr.per_query_score)}>
                                    {Math.round(pr.per_query_score)}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-4 mb-3 font-mono text-[11px]">
                                  {ATTR_KEYS.map(([k, l]) => {
                                    const invert = k.startsWith("attr_5") || k.startsWith("attr_6");
                                    return (
                                      <div key={k} className="flex items-center gap-1.5">
                                        <AttrDot on={pr[k]} invert={invert} />
                                        <span className="text-[color:var(--text-secondary)]">{l}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                                {pr.reasoning && (
                                  <div className="text-xs text-[color:var(--text-secondary)] leading-relaxed">
                                    <span className="text-[color:var(--text-muted)]">Judge: </span>
                                    {pr.reasoning}
                                  </div>
                                )}
                                {pr.cited_urls && pr.cited_urls.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1.5">
                                    {pr.cited_urls.slice(0, 5).map((u) => (
                                      <a
                                        key={u}
                                        href={u}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[10px] font-mono hover:underline truncate max-w-[280px]"
                                        style={{ color: "var(--brand-sage-deep)" }}
                                      >
                                        {u.replace(/^https?:\/\//, "").slice(0, 45)}
                                        {u.length > 60 ? "…" : ""}
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
